if (!customElements.get('contact-form-section')) {
  const SUCCESS_MESSAGE = "Thanks for contacting us. We'll get back to you as soon as possible.";
  const ERROR_MESSAGE = 'Something went wrong. Please try again.';

  class ContactFormSection extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-contact-form]');
      if (!this.form) return;

      this.submitBtn = this.querySelector('[data-contact-submit]');
      this.toast = this.querySelector('[data-contact-toast]');

      this.googleScriptUrl = this.dataset.googleScriptUrl ?? '';
      this.sendEmail = this.dataset.sendEmail === 'true';
      this.sheetFrom = this.dataset.sheetFrom ?? '';

      this.form.addEventListener('submit', this.handleSubmit.bind(this));
    }

    async handleSubmit(event) {
      event.preventDefault();

      // Keep valid fields, clear only the invalid ones, then let the browser flag them.
      if (!this.form.checkValidity()) {
        this.clearInvalidFields();
        this.form.reportValidity();
        return;
      }

      this.setLoading(true);

      const sheetOk = await this.submitToGoogleSheet();

      if (this.sendEmail) {
        await this.sendEmailNotification();
      }

      this.setLoading(false);

      if (sheetOk) {
        this.rememberIdentity();
        this.showToast(SUCCESS_MESSAGE);
        this.form.reset();
      } else {
        this.showToast(ERROR_MESSAGE, true);
      }
    }

    async submitToGoogleSheet() {
      if (!this.googleScriptUrl) return true;

      const payload = {
        formType: 'contact',
        timestamp: new Date().toISOString(),
        name: this.getValue('#ContactForm-name'),
        email: this.getValue('#ContactForm-email'),
        phone: this.getValue('#ContactForm-phone'),
        enquiry: this.getValue('#ContactForm-enquiry'),
        comment: this.getValue('#ContactForm-message'),
        from: this.sheetFrom,
      };

      try {
        await fetch(this.googleScriptUrl, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'text/plain' },
          body: JSON.stringify(payload),
        });
        return true;
      } catch (error) {
        console.error('ContactForm: Google Sheet submission failed', error);
        return false;
      }
    }

    async sendEmailNotification() {
      try {
        await fetch(this.form.getAttribute('action'), {
          method: 'POST',
          body: new FormData(this.form),
          headers: { Accept: 'application/json' },
        });
      } catch (error) {
        console.error('ContactForm: Email notification failed', error);
      }
    }

    /**
     * Remembers the visitor so Browse Abandon has a ContactKey later.
     * Optional chaining keeps this a no-op if the helper is not loaded.
     */
    rememberIdentity() {
      window.ZdSfmcIdentity?.save({
        email: this.getValue('#ContactForm-email'),
        name: this.getValue('#ContactForm-name'),
        phone: this.getValue('#ContactForm-phone'),
      });
    }

    clearInvalidFields() {
      this.form.querySelectorAll('input, select, textarea').forEach((field) => {
        if (!field.checkValidity()) field.value = '';
      });
    }

    getValue(selector) {
      const el = this.querySelector(selector);
      return el ? el.value.trim() : '';
    }

    setLoading(isLoading) {
      if (!this.submitBtn) return;
      if (isLoading) {
        this.submitBtn.setAttribute('disabled', '');
        this.submitBtn.textContent =
          this.submitBtn.getAttribute('data-loading-text') || 'Sending...';
      } else {
        this.submitBtn.removeAttribute('disabled');
        this.submitBtn.textContent =
          this.submitBtn.getAttribute('data-default-text') || 'Submit Enquiry';
      }
    }

    showToast(message, isError = false) {
      if (!this.toast) return;

      this.toast.textContent = message;
      this.toast.classList.toggle('section-contact-form__toast--error', isError);
      this.toast.classList.add('section-contact-form__toast--visible');

      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        this.toast.classList.remove('section-contact-form__toast--visible');
      }, 4000);
    }

    disconnectedCallback() {
      clearTimeout(this.toastTimer);
    }
  }

  customElements.define('contact-form-section', ContactFormSection);
}
